import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, ExternalLink, Loader2, MapPin } from "lucide-react";
import { format } from "date-fns";
import { useBusiness } from "@/hooks/use-business";
import { getBaseUrl } from "@/lib/site";

type EventMediaItem = {
  url: string;
  type?: "image" | "video";
};

function googleMapsUrl(e: any) {
  const placeId = e?.venue_place_id;
  const lat = e?.venue_lat;
  const lng = e?.venue_lng;
  const address = e?.venue_address;
  if (placeId) return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(placeId)}`;
  if (typeof lat === "number" && typeof lng === "number") return `https://www.google.com/maps?q=${lat},${lng}`;
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return null;
}

function getEventMediaItems(event: any): { url: string; type: "image" | "video" }[] {
  const items = (event?.media_items as EventMediaItem[] | null | undefined) || null;
  if (Array.isArray(items) && items.length > 0) {
    return items.map((item) => {
      const url = item.url;
      const lower = (url || "").toLowerCase();
      const isVideo =
        lower.endsWith(".mp4") ||
        lower.endsWith(".webm") ||
        lower.endsWith(".mov") ||
        lower.endsWith(".avi") ||
        lower.endsWith(".mkv");
      return {
        url,
        type: item.type ?? (isVideo ? "video" : "image"),
      };
    });
  }

  if (event?.cover_image_url) {
    const lower = String(event.cover_image_url).toLowerCase();
    const isVideo =
      lower.endsWith(".mp4") ||
      lower.endsWith(".webm") ||
      lower.endsWith(".mov") ||
      lower.endsWith(".avi") ||
      lower.endsWith(".mkv");
    return [
      {
        url: event.cover_image_url,
        type: isVideo ? "video" : "image",
      },
    ];
  }

  return [];
}

export default function EventPreview() {
  const { id } = useParams();
  const { business } = useBusiness();
  const sb = supabase as any;

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      const { data, error } = await sb.from("events").select("*").eq("id", id).single();
      if (error) setError(error.message);
      setEvent(data);
      setLoading(false);
    };
    run();
  }, [id]);

  const mediaItems = getEventMediaItems(event);
  const hero = mediaItems[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-background p-6">
        <p className="text-sm text-destructive">Failed to load preview: {error || "Not found"}</p>
      </div>
    );
  }

  const start = event.starts_at ? format(new Date(event.starts_at), "MMM d, yyyy • h:mm a") : "";
  const end = event.ends_at ? format(new Date(event.ends_at), "MMM d, yyyy • h:mm a") : null;
  const origin = getBaseUrl();
  const businessSlug = business?.slug || "";
  const publicUrl = businessSlug ? `${origin}/events/${businessSlug}/${event.slug}` : "";

  const maps = googleMapsUrl(event);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl p-4 sm:p-8">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Badge className="capitalize">{event.status}</Badge>
            {event.capacity ? <Badge variant="outline">Capacity: {event.capacity}</Badge> : null}
          </div>
          {event.status === "published" && publicUrl ? (
            <Button onClick={() => window.open(publicUrl, "_blank")}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Open public page
            </Button>
          ) : (
            <Button variant="outline" onClick={() => window.close()}>
              Close
            </Button>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-soft">
          {hero ? (
            hero.type === "video" ? (
              <video
                src={hero.url}
                className="h-56 w-full object-cover"
                muted
                playsInline
                autoPlay
                loop
              />
            ) : (
              <img src={hero.url} alt={event.title} className="h-56 w-full object-cover" />
            )
          ) : (
            <div className="h-56 w-full bg-gradient-to-br from-primary/15 via-primary/5 to-background flex items-center justify-center">
              <Calendar className="h-12 w-12 text-primary" />
            </div>
          )}

          <div className="p-6">
            <p className="text-2xl font-bold text-foreground">{event.title}</p>
            <p className="text-sm text-muted-foreground mt-2">{start}{end ? ` – ${end}` : ""}</p>
            {event.venue_name ? <p className="text-sm text-muted-foreground mt-1">{event.venue_name}</p> : null}
            {event.venue_address ? <p className="text-sm text-muted-foreground mt-1">{event.venue_address}</p> : null}
            {maps ? (
              <a
                className="inline-flex items-center gap-2 text-sm text-primary mt-3 hover:underline"
                href={maps}
                target="_blank"
                rel="noreferrer"
              >
                <MapPin className="h-4 w-4" />
                Open in Google Maps
              </a>
            ) : null}

            {event.description ? (
              <div className="mt-4 prose prose-sm max-w-none text-foreground">
                <p>{event.description}</p>
              </div>
            ) : null}

            <div className="mt-6 rounded-xl border border-border bg-accent/30 p-4">
              <p className="font-semibold text-foreground">Registration</p>
              {event.status === "published" ? (
                <>
                  <p className="text-sm text-muted-foreground mt-1">
                    Registration happens on the public event page.
                  </p>
                  <div className="mt-3">
                    <Button onClick={() => window.open(publicUrl, "_blank")} disabled={!publicUrl}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Open registration page
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mt-1">
                    Publish this event to enable registration.
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Tip: open <span className="font-medium text-foreground">Edit</span> and set status to{" "}
                    <span className="font-medium text-foreground">published</span>.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


